import { schema, types } from 'papr'
import { BADGE_TYPES, PROVIDERS, ROLES } from '../shared'

/**
 * The status of the Pro subscription in Paddle.
 *
 * @see https://developer.paddle.com/reference/ZG9jOjI1MzU0MDI2-subscription-status-reference
 */
export const PRO_PADDLE_SUBSCRIPTION_STATUS = [
	'active' as const,
	'past_due' as const,
	'deleted' as const,
]
/**
 * The status of the Pro subscription in Paddle.
 */
export type ProPaddleSubscriptionStatus = (typeof PRO_PADDLE_SUBSCRIPTION_STATUS)[number]

export const PRO_PADDLE_SUBSCRIPTION_CANCELATION_REASON = [
	'dispute' as const,
	'user-canceled' as const,
	'user-deleted' as const,
]
export type ProPaddleSubscriptionCancelationReason =
	(typeof PRO_PADDLE_SUBSCRIPTION_CANCELATION_REASON)[number]

export const PRO_PADDLE_BILLING_INTERVAL = ['month' as const, 'year' as const]
export type ProPaddleBillingInterval = (typeof PRO_PADDLE_BILLING_INTERVAL)[number]

const PRO_DISCOUNT_TYPES = ['flat' as const, 'percentage' as const]

export const userSchema = schema(
	{
		name: types.string({ required: true }),
		username: types.string({ required: true }),
		email: types.string({ required: true }),
		bio: types.string(),
		isBanned: types.boolean(),
		isDeactivated: types.boolean(),
		// TODO: this name is confusing. migrate it to isShadowBanned
		iSB: types.boolean(),
		// TODO: this enum is incomplete
		role: types.enum(ROLES),
		series: types.objectId(),
		socialMedia: types.object({
			website: types.string(),
			github: types.string(),
			twitter: types.string(),
			instagram: types.string(),
			facebook: types.string(),
			stackoverflow: types.string(),
			linkedin: types.string(),
			youtube: types.string(),
			bluesky: types.string(),
		}),

		tagline: types.string(),
		provider: types.enum(PROVIDERS),
		unverifiedEmail: types.string(),
		magicLinkId: types.string(),
		location: types.string(),
		photo: types.string(),
		coverImage: types.string(),
		accounts: types.array(
			types.object({
				name: types.string(),
				accessToken: types.string(),
				refreshToken: types.string(),
			}),
		),
		isUsernameChanged: types.boolean(),
		password: types.string(),
		isAdmin: types.boolean(),
		accountOnHold: types.boolean(),

		totalPoints: types.number(),
		timeZone: types.string(),
		bioMarkdown: types.string(),
		upvotedPosts: types.array(types.objectId()),
		downvotedPosts: types.array(types.objectId()),
		upvotedResponses: types.array(types.string()),
		downvotedResponses: types.array(types.string()),
		answerRewardsWon: types.array(types.objectId()),
		upvotedAMAs: types.array(types.objectId()),
		upvotedAMAQuestions: types.array(types.string()),
		upvotedAMAResponses: types.array(types.string()),
		following: types.array(types.objectId()),
		followers: types.array(types.objectId()),
		numFollowers: types.number(),
		numFollowing: types.number(),
		postsCommentedOn: types.array(types.objectId()),
		storiesCreated: types.array(types.objectId()),
		linksShared: types.array(types.objectId()),
		questionsAsked: types.array(types.objectId()),
		mentorOf: types.array(types.objectId()),
		pendingMentorOf: types.array(types.objectId()),
		pendingManagerOf: types.array(
			types.object({
				node: types.objectId(),
				message: types.string(),
			}),
		),
		savedTags: types.array(types.string()),
		emailPreferences: types.object({
			hnWeekly: types.boolean(),
			activityNotifications: types.boolean(),
			hashnodeUpdates: types.boolean(),
			generalAnnouncements: types.boolean(),
			amaUpdates: types.boolean(),
			directQuestionNotifications: types.boolean(),
			bookmarkReminderNotifications: types.boolean(),
			monthlyBlogStats: types.boolean(),
			newFollowersWeekly: types.boolean(),
			referralNotifications: types.boolean(),
		}),
		pushNotificationPreferences: types.object({
			postComment: types.boolean(),
			postWatch: types.boolean(),
			commentReply: types.boolean(),
			weeklyDigest: types.boolean(),
			userUpvote: types.boolean(),
			pollEnd: types.boolean(),
			ama: types.boolean(),
			mentions: types.boolean(),
			dndActive: types.boolean(),
		}),
		expoPushNotificationPreferences: types.object({
			postComment: types.boolean(),
			commentReply: types.boolean(),
			featured: types.boolean(),
			featureDigest: types.boolean(),
			reacted: types.boolean(),
			mentions: types.boolean(),
			dndActive: types.boolean(),
		}),
		isChangelogUnsubscribed: types.boolean(),
		bookmarkReminderFrequency: types.enum(['weekly' as const, 'daily' as const]),
		numCreatedPosts: types.number(),
		dateJoined: types.date(),
		appreciations: types.array(
			types.object({
				badge: types.objectId(),
				count: types.number(),
			}),
		),
		totalUpvotesReceived: types.number(),
		knowsAbout: types.array(types.objectId()),
		nodesFollowing: types.array(types.objectId()),
		postsFollowing: types.array(types.objectId()),
		collectionsFollowing: types.array(types.objectId()),
		pinnedNodes: types.array(types.objectId()),
		status: types.number(),
		isNewEntity: types.boolean(),
		unreadNotifications: types.number(),
		unreadComments: types.number(),
		unreadReactions: types.number(),
		unreadMentions: types.number(),
		unreadNewPosts: types.number(),
		contributions: types.array(
			types.object({
				node: types.objectId(),
				count: types.number(),
			}),
		),
		totalContributionCount: types.number(),
		views: types.number(),
		onboardingDone: types.boolean(),
		showWelcomeCard: types.boolean(),
		isDevmagUser: types.boolean(),
		showStoryInterstitial: types.boolean(),
		feedVisitDate: types.date(),
		badgesAwarded: types.array(
			types.object({
				period: types.array(types.string()),
				badge: types.objectId(),
			}),
		),
		dynamicBadges: types.array(
			types.object({
				period: types.string(),
				badge: types.objectId(),
				node: types.objectId(),
				type: types.enum(BADGE_TYPES),
				points: types.object({
					numPosts: types.number(),
					numAnswers: types.number(),
					numUpvotes: types.number(),
					numAppreciation: types.number(),
					total: types.number(),
				}),
			}),
		),
		managerOf: types.array(types.objectId()),
		tagManagerOf: types.array(types.objectId()),
		blacklistedWidgets: types.array(types.string()),
		focus: types.array(types.objectId()),
		featuredPosts: types.array(types.objectId()),
		beingFollowed: types.boolean(),
		followsBack: types.boolean(),
		hasActiveCollections: types.boolean(),
		appliedForOriginals: types.boolean(),
		slot: types.string(), //applicable for users of type bots for post automation.
		oneSignalId: types.string(),
		availableFor: types.string(),
		showEmailOnProfile: types.boolean(),
		directQuestionsCount: types.number(),
		isEvangelist: types.boolean(),
		wallets: types.object({
			xlm: types.string(),
		}),
		nonProfit: types.objectId(),
		amountDonatedToNonProfit: types.object({
			amount: types.number(),
			unit: types.string(),
		}),
		signature: types.string(), // For Digital Signature of the user (used by TalkJS)
		unreadMessages: types.number(),
		hasSeenMessages: types.boolean(),
		isTeamConversationSet: types.boolean(),
		numReactions: types.number(),
		numPosts: types.number(),
		numResponses: types.number(),
		numSeries: types.number(),
		publicationEnabled: types.boolean(),
		publicationDomain: types.string(),
		publicationAccessGivenBy: types.objectId(),
		hashnodeDevInvitesUsed: types.number(),
		blogAccessRequested: types.boolean(),
		draftsCount: types.number(),
		blogWidgetCollapsed: types.boolean(),
		hasAccessToTeamPublication: types.boolean(),
		hasBetaAccess: types.boolean(),
		blogHandle: types.string(),
		notifyAboutLaunch: types.boolean(),
		unsubscribeCode: types.string(),
		referredBy: types.objectId(),
		isAmbassador: types.boolean(),
		isEligibleToBecomeAmbassador: types.boolean(),
		hasGoldRing: types.boolean(),
		referralCount: types.number(),
		payments: types.array(
			types.object({
				method: types.string(),
				isDefault: types.boolean(),
			}),
		),
		customer: types.string(),
		requestedAccessToPayments: types.boolean(),
		expoPushToken: types.string(), // used for push notifications to identify device
		persona: types.string(),
		// TODO: strict types
		personaDetails: types.objectGeneric(types.string()),
		newOnboarding: types.boolean(),
		// TODO: ?
		isEligibleFor2021EoyReview: types.boolean(),
		twitterConnectProfile: types.objectGeneric(types.string()),
		areEmailsSuppressed: types.boolean(),
		betaFeatures: types.objectGeneric(types.boolean()),
		workOSUserId: types.string(),
		pro: types.object({
			/**
			 * Whether the user should have access to pro features.
			 */
			hasProAccess: types.boolean({ required: true }),
			updatedAt: types.date({ required: true }),
			/**
			 * Data from Paddle.
			 */
			paddle: types.object(
				{
					/**
					 * The ID of the pro subscription in Paddle.
					 */
					subscriptionId: types.number({ required: true }),
					/**
					 * The two-letter ISO country code of the customer.
					 * Can be used to calculate prices including the correct taxes.
					 *
					 * @example 'US'
					 */
					countryCode: types.string(),
					/**
					 * The status of the pro subscription in Paddle.
					 *
					 * @see https://developer.paddle.com/reference/ZG9jOjI1MzU0MDI2-subscription-status-reference
					 */
					status: types.enum(PRO_PADDLE_SUBSCRIPTION_STATUS, {
						required: true,
					}),
					/**
					 * The date when the user signed up for pro.
					 */
					signUpDate: types.date({ required: true }),
					billingInterval: types.enum(PRO_PADDLE_BILLING_INTERVAL, {
						required: true,
					}),
					/**
					 * The date when the next payment will be charged.
					 * This is when the next billing cycle starts.
					 */
					nextBillDate: types.date(),
					/**
					 * The date the pro subscription has been canceled.
					 */
					canceledAt: types.date(),
					/**
					 * The date the cancelation will actually take effect.
					 * Before this date, the status 'deleted' just means it will be deleted at this date.
					 */
					cancelationEffectiveDate: types.date(),
					/**
					 * The reason why the user canceled the pro subscription.
					 */
					cancelationReason: types.enum(PRO_PADDLE_SUBSCRIPTION_CANCELATION_REASON),
					discount: types.object({
						couponCode: types.string({ required: true }),
						isRecurring: types.boolean({ required: true }),
						type: types.enum(PRO_DISCOUNT_TYPES, { required: true }),
						/**
						 * The amount of the discount.
						 * If the type is 'percentage', this is the percentage (0.5 -> 50%).
						 * If the type is 'flat', this is the amount in USD.
						 */
						amount: types.number({ required: true }),
					}),
				},
				{ required: true },
			),
			// Check this for more details on the things we're storing: https://discord.com/developers/docs/resources/user#user-object-user-structure
			discord: types.object({
				userId: types.string(),
				username: types.string(),
				discriminator: types.string(),
			}),
		}),
		freeTrial: types.object({
			hasFreeTrialAccess: types.boolean({ required: true }),
			createdAt: types.date({ required: true }),
			updatedAt: types.date(),
		}),
	},
	{
		timestamps: true,
		defaults: {
			isDeactivated: false,
			isBanned: false,
			accountOnHold: false,
			iSB: false,
			numFollowers: 0,
			numFollowing: 0,
			emailPreferences: {
				hnWeekly: true,
				activityNotifications: true,
				hashnodeUpdates: true,
				generalAnnouncements: true,
				amaUpdates: true,
				directQuestionNotifications: true,
				bookmarkReminderNotifications: true,
				monthlyBlogStats: true,
				newFollowersWeekly: true,
				referralNotifications: true,
			},
			pushNotificationPreferences: {
				postComment: true,
				postWatch: true,
				commentReply: true,
				weeklyDigest: true,
				userUpvote: true,
				pollEnd: true,
				ama: true,
				mentions: true,
				dndActive: false,
			},
			expoPushNotificationPreferences: {
				postComment: true,
				commentReply: true,
				featured: true,
				featureDigest: true,
				reacted: true,
				mentions: true,
				dndActive: false,
			},
			isChangelogUnsubscribed: false,
			bookmarkReminderFrequency: 'weekly' as const,
			numCreatedPosts: 0,
			dateJoined: new Date(),
			// appreciations: [
			//   {
			//     count: 0,
			//   },
			// ],
			totalUpvotesReceived: 0,
			status: 0,
			unreadNotifications: 0,
			unreadComments: 0,
			unreadReactions: 0,
			unreadMentions: 0,
			unreadNewPosts: 0,
			// contributions: [
			//   {
			//     count: 0,
			//   },
			// ],
			totalContributionCount: 0,
			views: 0,
			showWelcomeCard: true,
			isDevmagUser: false,
			showStoryInterstitial: true,
			// dynamicBadges: [
			//   {
			//     points: {
			//       numPosts: 0,
			//       numAnswers: 0,
			//       numUpvotes: 0,
			//       numAppreciation: 0,
			//       total: 0,
			//     },
			//   },
			// ],
			showEmailOnProfile: false,
			directQuestionsCount: 0,
			isEvangelist: false,
			amountDonatedToNonProfit: {
				amount: 0,
			},
			unreadMessages: 0,
			hasSeenMessages: false,
			isTeamConversationSet: false,
			hashnodeDevInvitesUsed: 0,
			draftsCount: 0,
			notifyAboutLaunch: false,
			isAmbassador: false,
			isEligibleToBecomeAmbassador: false,
			hasGoldRing: false,
			referralCount: 0,
			// payments: [
			//   {
			//     isDefault: false,
			//   },
			// ],
			requestedAccessToPayments: false,
			newOnboarding: false,
			areEmailsSuppressed: false,
			betaFeatures: {},
			// Needed to add this, as we have some  non-null assertion (!) in the frontend code
			socialMedia: {},
			wallets: {},
		},
	},
)

export type UserDocument = (typeof userSchema)[0]
export type UserSchemaOptions = (typeof userSchema)[1]
